/**
 * Plans / agenda over the live E2E relay (v0.6.0) — pure, testable helpers.
 *
 * AppState uses these so live mode validates `plan.response` exactly like the
 * daemon's `/plans` + `/agenda` HTTP API: a malformed response becomes a real
 * error (never a partial/fake day), delete only succeeds after an explicit
 * acknowledgement, and a failed daemon message surfaces verbatim.
 *
 * The controller call is injected (`PlanLiveRequester`) so AppState is the only
 * place that knows about `LiveRelayController` — that is the seam if the relay
 * transport is swapped, and it keeps the helpers testable without a socket.
 */
import type { PlanManagementOp } from '@luminis/protocol';

import {
  agendaFromUnknown,
  assignmentsFromUnknown,
  isAssignmentAck,
  isPlanDeleteAcknowledged,
  planCreateBody,
  planMutationFromUnknown,
  plansFromUnknown,
  type AgendaAssignment,
  type AgendaView,
  type Plan,
  type PlanDraft,
} from '../agenda';
import { isValidLocalDate } from '../agenda';

export interface PlanLiveResult {
  ok: boolean;
  data?: unknown;
  error?: { code: string; message: string };
}

/** Fields carried alongside a `plan.request` operation. */
export interface PlanLiveRequest {
  /** Required by update/done/delete. */
  id?: string;
  /** create/update body (text/notes/start_at/due_at/expires_at). */
  body?: Record<string, unknown>;
  /** Only for `agenda`; `YYYY-MM-DD`; omitted = daemon's local today. */
  date?: string;
}

/** Injected transport: `LiveRelayController.requestPlan` in production. */
export type PlanLiveRequester = (
  op: PlanManagementOp,
  request?: PlanLiveRequest,
) => Promise<PlanLiveResult>;

/**
 * The two assignment-visibility ops on `plan.request` (v0.6.0):
 * `dismiss_assignment` hides one row from 议程 and `restore_assignment` un-hides
 * it. They carry only a bounded assignment id (no body/date) and return
 * `{ ok: true, assignments }` with the same `ApiAgendaAssignment` rows as the
 * agenda. Derived from the protocol op union so the wire contract stays single
 * sourced.
 */
export type AssignmentManagementOp = Extract<
  PlanManagementOp,
  'dismiss_assignment' | 'restore_assignment'
>;

/** Injected transport: `LiveRelayController.requestAssignment` in production. */
export type AssignmentLiveRequester = (
  op: AssignmentManagementOp,
  id: string,
) => Promise<PlanLiveResult>;

/** Fixed-message error text for a failed daemon response (code is appended). */
export function planLiveRequestError(result: PlanLiveResult, fallback: string): Error {
  const message = result.error?.message ?? fallback;
  return new Error(
    result.error?.code === undefined ? message : `${message}（${result.error.code}）`,
  );
}

async function requireOkData(
  work: () => Promise<PlanLiveResult>,
  fallback: string,
): Promise<unknown> {
  const result = await work();
  if (!result.ok) throw planLiveRequestError(result, fallback);
  return result.data;
}

/** `{ plans }` / bare array (list + post-delete remaining list). */
function parsePlanList(data: unknown, label: string): Plan[] {
  const plans = plansFromUnknown(data);
  if (plans === null) throw new Error(`daemon ${label} 返回了意外数据`);
  return plans;
}

/** `{ plan }` (create/done/update) or a full list (defensive). */
function parsePlanMutation(data: unknown, id: string | undefined, label: string): Plan {
  const plan = planMutationFromUnknown(data, id);
  if (plan === null) throw new Error(`daemon ${label} 返回了意外数据`);
  return plan;
}

/** The selected agenda day; the daemon resolves an omitted date itself. */
export async function requestAgendaLive(
  request: PlanLiveRequester,
  date?: string,
): Promise<AgendaView> {
  if (date !== undefined && !isValidLocalDate(date)) {
    throw new Error('议程日期格式无效（YYYY-MM-DD）');
  }
  const data = await requireOkData(
    () => request('agenda', date === undefined ? {} : { date }),
    '议程请求失败',
  );
  const agenda = agendaFromUnknown(data);
  if (agenda === null) throw new Error('daemon /agenda 返回了意外数据');
  return agenda;
}

/** Active plan list (`GET /plans` parity). */
export async function requestPlanListLive(request: PlanLiveRequester): Promise<Plan[]> {
  return parsePlanList(await requireOkData(() => request('list'), '清单列表请求失败'), '/plans');
}

/** Create one plan; returns the daemon-created row (never an optimistic row). */
export async function requestPlanCreateLive(
  request: PlanLiveRequester,
  draft: PlanDraft,
): Promise<Plan> {
  const data = await requireOkData(
    () => request('create', { body: planCreateBody(draft) }),
    '创建清单请求失败',
  );
  return parsePlanMutation(data, undefined, '/plans');
}

/** Mark a plan done (idempotent in the daemon; `completed_at` is kept). */
export async function requestPlanDoneLive(request: PlanLiveRequester, id: string): Promise<Plan> {
  const data = await requireOkData(() => request('done', { id }), '完成清单请求失败');
  return parsePlanMutation(data, id, '/plans/:id/done');
}

/**
 * Delete a plan. Only an explicit `{ ok: true, plans }` acknowledgement counts;
 * returns the remaining active list when the daemon includes it.
 */
export async function requestPlanDeleteLive(
  request: PlanLiveRequester,
  id: string,
): Promise<Plan[] | null> {
  const data = await requireOkData(() => request('delete', { id }), '删除清单请求失败');
  if (!isPlanDeleteAcknowledged(data)) throw new Error('daemon 未确认删除清单');
  return plansFromUnknown(data);
}

/** Shared dismiss/restore path: only an explicit ack + valid rows are success. */
async function requestAssignmentMutationLive(
  request: AssignmentLiveRequester,
  op: AssignmentManagementOp,
  id: string,
  fallback: string,
): Promise<AgendaAssignment[]> {
  const data = await requireOkData(() => request(op, id), fallback);
  if (!isAssignmentAck(data)) throw new Error('daemon 未确认作业操作');
  const assignments = assignmentsFromUnknown(data);
  if (assignments === null) throw new Error('daemon 返回了意外的作业数据');
  return assignments;
}

/**
 * Hide one assignment from 议程 (persisted by the daemon; it never touches
 * Learn). Returns the daemon's remaining unsubmitted list — only after the
 * explicit `{ ok: true }` acknowledgement.
 */
export async function requestAssignmentDismissLive(
  request: AssignmentLiveRequester,
  id: string,
): Promise<AgendaAssignment[]> {
  return requestAssignmentMutationLive(request, 'dismiss_assignment', id, '隐藏作业请求失败');
}

/** Un-hide a previously dismissed assignment (same ack/validation rules). */
export async function requestAssignmentRestoreLive(
  request: AssignmentLiveRequester,
  id: string,
): Promise<AgendaAssignment[]> {
  return requestAssignmentMutationLive(request, 'restore_assignment', id, '恢复作业请求失败');
}

/** Availability of the 「议程」 page for the current transport. */
export interface AgendaLiveAvailability {
  available: boolean;
  reason?: string;
}

/**
 * Demo mode always works against the clearly-labelled local demo store. Live
 * mode needs pairing + an enabled E2E relay plan; a temporarily offline relay
 * stays "available" so the request path surfaces the real daemon error instead
 * of hiding the page.
 */
export function agendaLiveAvailability(input: {
  demoMode: boolean;
  paired: boolean;
  relayEnabled: boolean;
  relayReason?: string;
}): AgendaLiveAvailability {
  if (input.demoMode) return { available: true };
  if (!input.paired) {
    return { available: false, reason: '议程需要通过已配对的 daemon 访问（请先配对）。' };
  }
  if (!input.relayEnabled) {
    return {
      available: false,
      reason: `live relay 未就绪，无法访问 daemon 的议程接口：${input.relayReason ?? '未配置'}`,
    };
  }
  return { available: true };
}
